import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const navItems = [
  { label: 'Home', href: '/' },
  { label: 'Capabilities', href: '#solutions' },
  { label: 'How It Works', href: '#approach' },
  { label: 'Solutions', href: '/solutions' },
  { label: 'About', href: '/about' },
  { label: 'FAQ', href: '/faq' },
  { label: 'Contact', href: '/contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    if (!href.startsWith('#')) return;
    e.preventDefault();
    const id = href.slice(1);
    if (location.pathname === '/') {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/');
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
      }, 400);
    }
    setMenuOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-[#F9F9F7] border-b border-[#061e45]/10' : 'bg-transparent'
      }`}
      style={{ height: '72px' }}
    >
      <div className="max-w-[1400px] mx-auto px-8 h-full flex items-center justify-between">
        {/* Logo */}
        <a href="/" className="flex items-center gap-3 cursor-pointer">
          {/* White logo — shown on transparent navbar */}
          <img
            src="/images/logo-white.png"
            alt="Fore Capital"
            className={`h-14 w-auto object-contain brightness-0 invert absolute transition-opacity duration-300 ${scrolled ? 'opacity-0' : 'opacity-100'}`}
          />
          {/* Color logo — shown when scrolled */}
          <img
            src="/images/logo-color.png"
            alt="Fore Capital"
            className={`h-14 w-auto object-contain transition-opacity duration-300 ${scrolled ? 'opacity-100' : 'opacity-0'}`}
          />
        </a>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-6 text-[15px] text-center">
          {navItems.map((item) => (
            <a
              key={item.label}
              href={item.href}
              onClick={(e) => handleNavClick(e, item.href)}
              className={`text-[11px] uppercase tracking-[0.18em] font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                scrolled ? 'text-[#1A1A1A]/70 hover:text-[#061e45]' : 'text-white/80 hover:text-white'
              }`}
            >
              {item.label}
            </a>
          ))}
        </nav>

        {/* CTA */}
        <div className="hidden md:flex items-center gap-6">
          <a
            href="/apply"
            className={`whitespace-nowrap text-[11px] uppercase tracking-[0.18em] font-medium px-5 py-2.5 transition-all duration-200 cursor-pointer ${
              scrolled
                ? 'bg-[#061e45] text-white'
                : 'bg-[#d3b366] text-[#061e45] hover:bg-white hover:text-[#061e45]'
            }`}
          >
            Get Started
          </a>
        </div>

        {/* Mobile Hamburger */}
        <button
          className="md:hidden flex flex-col gap-1.5 cursor-pointer"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <span className={`block w-6 h-px transition-all duration-200 ${scrolled ? 'bg-[#061e45]' : 'bg-white'}`} />
          <span className={`block w-6 h-px transition-all duration-200 ${scrolled ? 'bg-[#061e45]' : 'bg-white'}`} />
          <span className={`block w-6 h-px transition-all duration-200 ${scrolled ? 'bg-[#061e45]' : 'bg-white'}`} />
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-[#F9F9F7] border-t border-[#1A1A1A]/10 px-8 py-6 flex flex-col gap-5">
          {navItems.map((item) => (
            <a
              key={item.label}
              href={item.href}
              onClick={(e) => handleNavClick(e, item.href)}
              className="text-[11px] uppercase tracking-[0.18em] font-medium text-[#1A1A1A]/70 hover:text-[#061e45] transition-colors cursor-pointer whitespace-nowrap"
            >
              {item.label}
            </a>
          ))}
          <a
            href="/apply"
            onClick={() => setMenuOpen(false)}
            className="whitespace-nowrap text-[11px] uppercase tracking-[0.18em] font-medium px-5 py-2.5 border border-[#a8894a] text-[#a8894a] text-center cursor-pointer"
          >
            Get Started
          </a>
        </div>
      )}
    </header>
  );
}
